﻿import { useMutation, useQuery } from '@tanstack/react-query';
import {
  deleteFavorite as deleteFavoriteFn,
  deleteSuperFavorite,
  getFavorite,
  postFavorite as postFavoriteFn,
  postSuperFavorite
} from '../api/CasinoApi';
import useFavoriteStore from '../stores/FavoriteStore';
import { useEffect, useState } from 'react';
import useIsLoggedIn from '@SharedScripts/Hooks/useIsLoggedIn';
import { FavoriteSessionKey } from '../Constants';
import { assertFavoriteSessionObject } from '../assertions';

const useFavorite = () => {

  const addFavoriteToStore = useFavoriteStore((state) => state.add);
  const removeFavoriteFromStore = useFavoriteStore((state) => state.remove);
  const favorites = useFavoriteStore((state) => state.favorites);
  const setInitialFavorite = useFavoriteStore((state) => state.setInitial);
  const [highlightsIds, setHighlightsIds] = useState<string[]>([]);
  const isLoggedIn = useIsLoggedIn();

  const favorite = useQuery({
    queryFn: getFavorite,
    queryKey: ['getFavorite'],
    enabled: isLoggedIn
  });

  const addFavoriteMutation = useMutation({
    mutationFn: (gameId: string) => postFavoriteFn(gameId),
    mutationKey: ['addFavorite'],
    onMutate: (gameId) => addFavoriteToStore(gameId),
    onError: (_ignoreError, gameId) => removeFavoriteFromStore(gameId)
  });

  const removeFavoriteMutation = useMutation({
    mutationFn: (gameId: string) => deleteFavoriteFn(gameId),
    mutationKey: ['removeFavorite'],
    onMutate: (gameId) => removeFavoriteFromStore(gameId),
    onError: (_ignoreError, gameId) => addFavoriteToStore(gameId)
  });

  const removeSuperFavoriteMutation = useMutation({
    mutationFn: (gameId: string) => deleteSuperFavorite(gameId),
    onSuccess: () => reloadFavorites()
  });

  const addSuperFavoriteMutation = useMutation({
    mutationFn: (gameId: string) => postSuperFavorite(gameId),
    onSuccess: () => reloadFavorites()
  });

  useEffect(() => {
    const favoriteFromStorage = sessionStorage.getItem(FavoriteSessionKey);
    if (favoriteFromStorage) {
      try {
        const favoriteSessionObject = JSON.parse(favoriteFromStorage);
        assertFavoriteSessionObject(favoriteSessionObject);

        const { type, id } = favoriteSessionObject;
        if (type === 'normal') {
          addFavorite(id);
        } else if (type === 'superFavorite') {
          addSuperFavorite(id);
        }

      } catch (ignore) {
        console.log('Failed to parse ' + FavoriteSessionKey);
      } finally {
        sessionStorage.removeItem(FavoriteSessionKey);
      }
    }
  }, []);

  useEffect(() => {
    if (!favorite.data) return;
    const favoriteEntityIds = favorite.data.favorites?.map((value) => value.entityId).filter(Boolean) as string[];
    setInitialFavorite(favoriteEntityIds);

    const highlightEntityIds = favorite.data.highLights?.map((value) => value.entityId).filter(Boolean) as string[];
    setHighlightsIds(highlightEntityIds);
  }, [favorite.data]);

  const reloadFavorites = () => {
    // queryClient.invalidateQueries({queryKey: ['getFavorite']});
    favorite.refetch();
  };

  useEffect(() => {
    if (isLoggedIn) return;

    setHighlightsIds([]);
    setInitialFavorite([]);
  }, [isLoggedIn]);

  const addFavorite = (gameId: string) => addFavoriteMutation.mutate(gameId);

  const removeFavorite = (gameId: string) => removeFavoriteMutation.mutate(gameId);

  const removeSuperFavorite = (gameId: string) => removeSuperFavoriteMutation.mutate(gameId);

  const addSuperFavorite = (gameId: string) => addSuperFavoriteMutation.mutate(gameId);

  return {
    favorites,
    highlightsIds,
    addFavorite,
    addSuperFavorite,
    removeFavorite,
    removeSuperFavorite,
    reloadFavorites,
  };
};

export default useFavorite;
